// Split a hosted table into two, or merge two hosted tables (task 2026-10-05 "a hosted table can
// be split or merged"; backend: routers/restructure.py, operate/restructure.py). A project
// manager builds the plan document from the hosted version's tables (GET .../versions/{n}),
// previews it (POST .../restructure/preview: row counts, conflicts, key links re-pointed),
// and applies it after retyping the database name (POST .../restructure, 202); the panel then
// follows the new version until the job is over (GET .../versions/{n}). A merge converts no
// values (columns carry no unit since N2, 2026-10-13).
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api";
import { Btn, ConfirmDialog, Spinner } from "../../components/Shared";
import { ErrorNote } from "../../components/Feedback";
import { buildPolling } from "./BuildPanel";

const box = { marginTop: 12, padding: "12px 16px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 };
const label = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#374151" };
const control = { padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", fontSize: 13 };
const row = { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 10 };

const fieldsOf = (t) => (t ? t.fields.filter(f => !f.surrogate).map(f => f.name) : []);

// The plan document (operate/restructure_plan.py) from the form state.
export function splitPlan(table, form) {
  const cols = fieldsOf(table);
  const where = c => form.places[c] || "child";
  const others = cols.filter(c => c !== form.key);
  return {
    op: "split", table: table.name, key: form.key,
    parent: { name: form.parent, columns: [form.key, ...others.filter(c => where(c) === "parent")] },
    child: { name: form.child, columns: [form.key, ...others.filter(c => where(c) === "child")] },
    discard: others.filter(c => where(c) === "discard"),
    on_conflict: form.onConflict, keep_old: form.keepOld,
  };
}

export function mergePlan(a, b, form) {
  const columns = {};
  fieldsOf(a).forEach(c => { columns[c] = form.mapping[c] || null; });
  const mapped = new Set(Object.values(columns).filter(Boolean));
  return {
    op: "merge", tables: [a.name, b.name], name: form.name, key: form.key, columns,
    discard: fieldsOf(b).filter(c => !mapped.has(c)),
    on_conflict: form.onConflict, keep_old: form.keepOld,
  };
}

function Preview({ p }) {
  return (
    <div data-testid="restructure-preview" style={box}>
      {(p.summary || []).map((s, i) => <div key={i}>{s}</div>)}
      {p.row_counts && (
        <div style={{ marginTop: 6 }}>
          Rows before: {Object.entries(p.row_counts.before).map(([t, n]) => `${t} ${n}`).join(" · ")}; after:{" "}
          {Object.entries(p.row_counts.after).map(([t, n]) => `${t} ${n}`).join(" · ")}
          {p.overlap != null ? ` (${p.overlap} key(s) in both tables)` : ""}
        </div>
      )}
      {p.foreign_keys && (
        <ul aria-label="Key links" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {["repointed", "moved", "added", "dropped"].flatMap(k => (p.foreign_keys[k] || []).map((f, i) => (
            <li key={`${k}-${i}`}>{k === "dropped" ? "Dropped" : k === "added" ? "Added" : k === "moved" ? "Moved" : "Re-pointed"}:{" "}
              {f.table}.{f.column} → {f.to}</li>
          )))}
        </ul>
      )}
      {(p.warnings || []).map((w, i) => <div key={i} style={{ color: "#92400e", marginTop: 4 }}>{w}</div>)}
      {(p.issues || []).length > 0 && (
        <ul aria-label="Rows to look at" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {p.issues.slice(0, 50).map((x, i) => (
            <li key={i} style={{ color: x.severity === "error" ? "#991b1b" : "#92400e" }}>{x.message}</li>
          ))}
        </ul>
      )}
      {(p.blocking || []).length > 0 && (
        <div role="alert" style={{ marginTop: 8, color: "#991b1b" }}>
          <strong>This cannot be applied:</strong>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{p.blocking.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export default function RestructurePanel({ ws, onChange }) {
  const hosting = ws.hosting;
  const projectId = ws.project_id;
  const [open, setOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [op, setOp] = useState("split");
  const [split, setSplit] = useState({ table: "", key: "", parent: "", child: "", places: {}, onConflict: "refuse", keepOld: false });
  const [merge, setMerge] = useState({ a: "", b: "", name: "", key: "", mapping: {}, onConflict: "keep_a", keepOld: false });
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(null);       // the version being applied
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!open || !hosting) return;
    api.get(`/projects/${projectId}/dbdesign/versions/${hosting.version}`)
      .then(v => setTables((v.design || {}).tables || []))
      .catch(setError);
  }, [open, projectId, hosting]);

  useEffect(() => {
    if (!running) return undefined;
    let live = true;
    let timer;
    const tick = async () => {
      try {
        const v = await api.get(`/projects/${projectId}/dbdesign/versions/${running.version}`);
        if (!live) return;
        if (v.status === "migrating") { timer = setTimeout(tick, buildPolling.intervalMs); return; }
        setRunning(null);
        setPreview(null);
        if (v.status === "failed") setError(new Error(`The ${running.op} failed; ${ws.database_name} is unchanged. See the build log above.`));
        onChangeRef.current();
      } catch (e) {
        if (!live) return;
        setError(e);
        timer = setTimeout(tick, buildPolling.intervalMs);
      }
    };
    timer = setTimeout(tick, buildPolling.intervalMs);
    return () => { live = false; clearTimeout(timer); };
  }, [running, projectId, ws.database_name]);

  const byName = useMemo(() => Object.fromEntries(tables.map(t => [t.name, t])), [tables]);
  if (!hosting) return null;

  const doc = () => (op === "split"
    ? splitPlan(byName[split.table], split)
    : mergePlan(byName[merge.a], byName[merge.b], merge));
  const ready = op === "split"
    ? split.table && split.key && split.parent && split.child
    : merge.a && merge.b && merge.name && merge.key;
  const call = async (fn) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const doPreview = () => call(async () => {
    setPreview(await api.post(`/projects/${projectId}/dbdesign/restructure/preview`, doc()));
  });
  const apply = () => call(async () => {
    setConfirming(false);
    const v = await api.post(`/projects/${projectId}/dbdesign/restructure`, { plan: doc(), confirm: ws.database_name });
    setRunning({ version: v.version, op });
    onChange();
  });
  const editSplit = (patch) => { setSplit(s => ({ ...s, ...patch })); setPreview(null); };
  const editMerge = (patch) => { setMerge(s => ({ ...s, ...patch })); setPreview(null); };
  const st = byName[split.table];
  const ma = byName[merge.a];
  const mb = byName[merge.b];

  return (
    <section aria-label="Split or merge" data-testid="restructure-panel" style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Split or merge a hosted table</h3>
        <Btn variant="secondary" onClick={() => setOpen(o => !o)}>
          {open ? "Close" : "Split or merge a table"}
        </Btn>
      </div>
      <p style={{ margin: "6px 0 0", fontSize: 13, color: "#374151" }}>
        Moves rows between tables of {ws.database_name} in one transaction, after a dump to S3: a split puts
        the repeated values in a parent table and keeps one child row per row; a merge joins two tables with
        the same meaning. Key links into the tables are re-pointed, and the design follows as a new version.
      </p>
      <ErrorNote error={error} />
      {running && <div role="status" style={box}><Spinner /> Applying the {running.op} to {ws.database_name}… data entry waits until it ends.</div>}
      {open && !running && (
        <div style={{ marginTop: 10 }}>
          <fieldset style={{ border: "none", padding: 0, margin: 0, display: "flex", gap: 16 }}>
            <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Operation</legend>
            <label><input type="radio" name="restructure-op" checked={op === "split"} onChange={() => { setOp("split"); setPreview(null); }} /> Split a table</label>
            <label><input type="radio" name="restructure-op" checked={op === "merge"} onChange={() => { setOp("merge"); setPreview(null); }} /> Merge two tables</label>
          </fieldset>

          {op === "split" && (
            <div>
              <div style={row}>
                <label style={label}>Table to split
                  <select style={control} value={split.table} onChange={e => editSplit({ table: e.target.value, key: "", places: {} })}>
                    <option value="">Choose…</option>
                    {tables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                </label>
                {st && (
                  <label style={label}>Key joining the parts
                    <select style={control} value={split.key} onChange={e => editSplit({ key: e.target.value })}>
                      <option value="">Choose…</option>
                      {fieldsOf(st).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                )}
                <label style={label}>Parent table (one row per key)
                  <input style={control} value={split.parent} onChange={e => editSplit({ parent: e.target.value })} />
                </label>
                <label style={label}>Child table (one row per row)
                  <input style={control} value={split.child} onChange={e => editSplit({ child: e.target.value })} />
                </label>
              </div>
              {st && split.key && (
                <div data-table-scroll style={{ marginTop: 10 }}>
                <table style={{ fontSize: 13, borderCollapse: "collapse" }}>
                  <caption style={{ textAlign: "left", fontWeight: 600 }}>Where each column goes</caption>
                  <tbody>
                    {fieldsOf(st).filter(c => c !== split.key).map(c => (
                      <tr key={c}>
                        <th scope="row" style={{ textAlign: "left", paddingRight: 12, fontWeight: 400 }}>{c}</th>
                        <td>
                          <select aria-label={`Column ${c} goes to`} style={control} value={split.places[c] || "child"}
                                  onChange={e => editSplit({ places: { ...split.places, [c]: e.target.value } })}>
                            <option value="parent">parent</option>
                            <option value="child">child</option>
                            <option value="discard">nowhere (values dropped)</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
              <div style={row}>
                <label style={label}>A key with different parent values
                  <select style={control} value={split.onConflict} onChange={e => editSplit({ onConflict: e.target.value })}>
                    <option value="refuse">refuse the split</option>
                    <option value="first">keep the first row's values</option>
                  </select>
                </label>
                <label style={{ ...label, flexDirection: "row", alignItems: "center" }}>
                  <input type="checkbox" checked={split.keepOld} onChange={e => editSplit({ keepOld: e.target.checked })} />
                  Keep the old table as {split.table || "\u003ctable>"}__old
                </label>
              </div>
            </div>
          )}

          {op === "merge" && (
            <div>
              <div style={row}>
                <label style={label}>First table (A)
                  <select style={control} value={merge.a} onChange={e => editMerge({ a: e.target.value, key: "", mapping: {} })}>
                    <option value="">Choose…</option>
                    {tables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                </label>
                <label style={label}>Second table (B)
                  <select style={control} value={merge.b} onChange={e => editMerge({ b: e.target.value, mapping: {} })}>
                    <option value="">Choose…</option>
                    {tables.filter(t => t.name !== merge.a).map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                </label>
                <label style={label}>Merged table
                  <input style={control} value={merge.name} onChange={e => editMerge({ name: e.target.value })} />
                </label>
                {ma && (
                  <label style={label}>Matching key (a column of A)
                    <select style={control} value={merge.key} onChange={e => editMerge({ key: e.target.value })}>
                      <option value="">Choose…</option>
                      {fieldsOf(ma).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                )}
              </div>
              {ma && mb && (
                <div data-table-scroll style={{ marginTop: 10 }}>
                <table style={{ fontSize: 13, borderCollapse: "collapse" }}>
                  <caption style={{ textAlign: "left", fontWeight: 600 }}>Which column of {mb.name} fills each column of {ma.name}</caption>
                  <tbody>
                    {fieldsOf(ma).map(c => (
                      <tr key={c}>
                        <th scope="row" style={{ textAlign: "left", paddingRight: 12, fontWeight: 400 }}>{c}</th>
                        <td>
                          <select aria-label={`Column of ${mb.name} for ${c}`} style={control} value={merge.mapping[c] || ""}
                                  onChange={e => editMerge({ mapping: { ...merge.mapping, [c]: e.target.value } })}>
                            <option value="">none (empty for B's rows)</option>
                            {fieldsOf(mb).map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
              {ma && mb && (
                <p style={{ fontSize: 13, color: "#374151" }}>
                  Columns of {mb.name} mapped to nothing are dropped with their values:{" "}
                  {mergePlan(ma, mb, merge).discard.join(", ") || "none"}.
                </p>
              )}
              <div style={row}>
                <label style={label}>A key in both tables with different values
                  <select style={control} value={merge.onConflict} onChange={e => editMerge({ onConflict: e.target.value })}>
                    <option value="keep_a">keep A's values</option>
                    <option value="keep_b">take B's values</option>
                    <option value="refuse">refuse the merge</option>
                  </select>
                </label>
                <label style={{ ...label, flexDirection: "row", alignItems: "center" }}>
                  <input type="checkbox" checked={merge.keepOld} onChange={e => editMerge({ keepOld: e.target.checked })} />
                  Keep the old tables as &lt;name&gt;__old
                </label>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <Btn variant="secondary" disabled={busy || !ready} onClick={doPreview}>Preview</Btn>
            {preview && preview.can_apply && (
              <Btn disabled={busy} onClick={() => setConfirming(true)}>Apply {op}</Btn>
            )}
          </div>
          {preview && <Preview p={preview} />}
        </div>
      )}
      {confirming && preview && (
        <ConfirmDialog title={`${op === "split" ? "Split" : "Merge"} in ${ws.database_name}?`} confirmWord={ws.database_name}
                       confirmLabel={`Apply ${op}`} onConfirm={apply} onClose={() => setConfirming(false)}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            {(preview.summary || []).join(" ")} It runs in one transaction; the tables are locked while it runs and
            data entry answers "maintenance". A dump is written to S3 first.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
